import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import Warning from '@mui/icons-material/Warning';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, buildRoute, defaultDataTransform, useDataFetcher, formatShortDateTime } from 'Utilities';
import { useEnums, useGlobalContext } from 'Context';
import { useHistory } from 'react-router-dom';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import useClientSearchParams from 'v3/Utilities/useClientSearchParams';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import './AssigningQueue.scss';


interface DropdownOptions {
  clients: { label: string; value: string }[];
  reviewers: { label: string; value: string }[];
  owners: { label: string; value: string }[];
  states: { label: string; value: string }[];
  specialties: { label: string; value: string }[];
  apContactPreferences: { label: string; value: string }[];
  tatPriorities: { label: string; value: string }[];
  tats: { label: string; value: string }[];
  lobs: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  proxiedByUsers: string[];
  appointmentStatuses: { label: string; value: string }[];
}


const renderSpecialtyCell = (params: any) => {
  const specialties: { displayName: string }[] = params.row.specialty ?? [];
  if (specialties.length === 0) return null;
  return (
    <span className="specialty-cell">
      <span>{specialties[0]?.displayName}</span>
      {specialties.length > 1 && (
        <span className="specialty-cell__extra">+{specialties.length - 1}</span>
      )}
    </span>
  );
};

const NUMBER_OF_REVIEWS_OPTIONS = [1, 2, 3, 4, 5].map(n => ({ label: String(n), value: String(n) }));
const NUMBER_OF_APS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => ({ label: String(n), value: String(n) }));

// 20 shared visible columns + extra hidden columns appended per table
const buildSharedColumns = (opts: DropdownOptions, userdata: any): any[] => {
  const isInternal = userdata?.isInternal ?? false;
  const isClient = userdata?.isClient ?? false;
  const columns: Array<{ _show?: boolean; [key: string]: any }> = [
  {
    field: 'stradixNumber',
    headerName: 'Stradix #',
    width: 188,
    hideable: false,
    pinnable: false,
    cellClassName: 'assigning-queue-cell-bold',
    renderCell: (params: any) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span>{params.value}</span>
        {params.row.rejected && (
          <Tooltip
            title={`Rejected by ${params.row.rejectedBy} - ${params.row.rejectionReason}`}
            placement="right"
          >
            <Warning fontSize="small" />
          </Tooltip>
        )}
      </span>
    ),
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimant',
    headerName: 'Claimant',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'client',
    headerName: 'Client',
    width: 200,
    hideable: true,
    _show: isInternal,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.clients} />,
  },
  {
    field: 'claimNumber',
    headerName: 'Claim/Client #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'referralDate',
    headerName: 'Referral Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
  },
  {
    field: 'dueDate',
    headerName: 'Client Due Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
  },
  {
    field: 'tatPriority',
    headerName: 'TAT Priority',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderCell: (params: any) =>
      params.value ? <StatusPill status={params.value} /> : null,
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />,
  },
  {
    field: 'benefitState',
    headerName: 'State',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.states} />,
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 180,
    hideable: true,
    _show: !isClient,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'intakeOwnerName',
    headerName: 'Intake Owner',
    width: 180,
    hideable: true,
    _show: !isClient,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'assigningOwnerName',
    headerName: 'Assignment Owner',
    width: 180,
    hideable: true,
    _show: !isClient,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'recentUser',
    headerName: 'Recent User',
    width: 180,
    hideable: true,
    _show: isInternal,
    sortable: false,
    filterable: false,
  },
  {
    field: 'numberOfReviews',
    headerName: '# of Reviews',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={NUMBER_OF_REVIEWS_OPTIONS} />,
  },
  {
    field: 'specialty',
    headerName: 'Requested Specialty',
    width: 200,
    hideable: true,
    sortable: false,
    type: 'singleSelect',
    renderCell: renderSpecialtyCell,
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.specialties} />,
  },
  {
    field: 'apContactPreference',
    headerName: 'AP Contact Pref.',
    width: 160,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />,
  },
  {
    field: 'numberOfAttendingProviders',
    headerName: '# of APs',
    width: 120,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={NUMBER_OF_APS_OPTIONS} />,
  },
  {
    field: 'subService',
    headerName: 'Sub Service',
    width: 220,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.subServices} />,
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 200,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.services} />,
  },
  {
    field: 'appointmentStatus',
    headerName: 'Status',
    width: 160,
    hideable: true,
    type: 'singleSelect',
    renderCell: (params: any) =>
      params.value ? <StatusPill status={params.value} /> : null,
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.appointmentStatuses} />,
  },
  {
    field: 'lob',
    headerName: 'LOB',
    width: 260,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.lobs} />,
  },
  {
    field: 'referralSource',
    headerName: 'Referrer',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'proxiedBy',
    headerName: 'Proxied By',
    width: 180,
    hideable: true,
    _show: isInternal,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.proxiedByUsers} />,
  },
  ];
  return columns
    .filter(({ _show }) => _show === undefined || _show)
    .map(({ _show, ...col }) => col);
};

const buildPeerReviewHiddenColumns = (opts: DropdownOptions): any[] => [
  {
    field: 'referralNumber',
    headerName: 'Referral #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'tat',
    headerName: 'TAT',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tats} />,
  },
  {
    field: 'employerName',
    headerName: 'Employer Name',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
];

const buildImeHiddenColumns = (opts: DropdownOptions): any[] => [
  {
    field: 'referralNumber',
    headerName: 'Referral #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'tat',
    headerName: 'TAT',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tats} />,
  },
  {
    field: 'claimantStreetAddress',
    headerName: 'Claimant Address',
    width: 400,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantCity',
    headerName: 'Claimant City',
    width: 260,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantState',
    headerName: 'Claimant State',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.states} />,
  },
  {
    field: 'claimantZipCode',
    headerName: 'Claimant Zip Code',
    width: 140,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantPhone',
    headerName: 'Claimant Phone #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantEmail',
    headerName: 'Claimant Email',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
];

const peerReviewColumnVisibility = {
  referralNumber: false,
  tat: false,
  employerName: false,
  intakeOwnerName: false,
};

const imeColumnVisibility = {
  referralNumber: false,
  tat: false,
  claimantStreetAddress: false,
  claimantCity: false,
  claimantState: false,
  claimantZipCode: false,
  claimantPhone: false,
  claimantEmail: false,
  intakeOwnerName: false,
};

const useAssigningQueueDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();

  const filterOptionsEndpoint = ENDPOINTS.assigningqueue.endpointString('FilterOptions');

  const [clientsList] = useDataFetcher<{ id: string; name: string }[]>(
    buildEndpoint(ENDPOINTS.clients.dropdownItems),
    [],
    defaultDataTransform,
  );
  const [filterOptions] = useDataFetcher<{
    owners: { id: string; name: string }[];
    reviewers: { id: string; name: string }[];
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
    proxiedByUsers: string[];
  }>(
    buildEndpoint(filterOptionsEndpoint),
    { owners: [], reviewers: [], services: [], subServices: [], proxiedByUsers: [] },
    defaultDataTransform,
    undefined,
    true,
    '3.0',
  );

  return {
    clients: (clientsList ?? []).map((c) => ({ label: c.name, value: c.id })).filter((c) => c.label && c.value),
    reviewers: (filterOptions?.reviewers ?? []).map((r) => ({ label: r.name, value: r.id })),
    owners: (filterOptions?.owners ?? []).map((o) => ({ label: o.name, value: o.id })),
    states: (enums?.state ? Object.values(enums.state) : []).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    specialties: Object.values(enums?.specialty ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    apContactPreferences: Object.values(enums?.apContact ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tatPriorities: Object.values(enums?.tatPriority ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tats: Object.values(enums?.tat ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    services: (filterOptions?.services ?? []).map((s) => ({ label: s.name, value: s.id })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({ label: s.name, value: s.id })),
    proxiedByUsers: filterOptions?.proxiedByUsers ?? [],
    appointmentStatuses: Object.values(enums?.appointmentStatus ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
  };
};

export const AssigningQueuePage: React.FC = () => {
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternal = userdata?.isInternal ?? false;
  const readEndpoint = buildEndpoint(ENDPOINTS.assigningqueue.read);
  const exportEndpoint = buildEndpoint(ENDPOINTS.assigningqueue.endpointString('ExportToCsv'));
  const dropdownOptions = useAssigningQueueDropdownOptions();

  const sharedColumns = React.useMemo(() => buildSharedColumns(dropdownOptions, userdata), [dropdownOptions, userdata]);
  const peerReviewHiddenCols = React.useMemo(() => buildPeerReviewHiddenColumns(dropdownOptions), [dropdownOptions]);
  const imeHiddenColumns = React.useMemo(() => buildImeHiddenColumns(dropdownOptions), [dropdownOptions]);
  const peerReviewColumns = React.useMemo(
    () => [...sharedColumns, ...peerReviewHiddenCols],
    [sharedColumns, peerReviewHiddenCols],
  );
  const imeColumns = React.useMemo(
    () => [...sharedColumns, ...imeHiddenColumns],
    [sharedColumns, imeHiddenColumns],
  );

  const peerReviewLegacyParams = React.useMemo(() => ({
    readEndpoint,
    searchParams: { excludeLineOfBusiness: { value: LineOfBusinessEnum.IME } },
    apiVersion: '3.0',
  }), [readEndpoint]);

  const imeLegacyParams = React.useMemo(() => ({
    readEndpoint,
    searchParams: { lineOfBusiness: { value: LineOfBusinessEnum.IME } },
    apiVersion: '3.0',
  }), [readEndpoint]);

  const { setSelectedClient, clientSearchParams } = useClientSearchParams();

  const handleRowClick = (params: any) => {
    const referralId = params.row?.referralId;
    if (referralId) {
      history.push(buildRoute(AddEditSubRoute.Referral, referralId, ROUTES.VIEW));
    }
  };

  return (
    <div className='assigningQueue'>
      <div className="main-grid-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Assigning Queue</h1>
          <div className="grids-header-client-selector">
            <ClientSelector containerClassName="client-selector--large" onClientChange={setSelectedClient} />
          </div>
        </div>
        <NewReferralButton text={userdata?.isInternal ? 'Add Case' : 'Add Referral'} startIcon={<AddIcon />} />
      </div>
      <div className="reviewers-content">
        <DataGrid
          onRowClick={handleRowClick}
          title='Peer Review Cases'
          gridId='assigningQueuePeerReview'
          enableUrlSync
          urlPrefix='pr'
          legacyParams={peerReviewLegacyParams}
          columns={peerReviewColumns}
          columnVisibilityModel={peerReviewColumnVisibility}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="AssigningQueue_PeerReview"
          defaultShowFilters={isInternal}
          enableAutoRefresh
        />
        <div className="assigning-queue-grid-gap" />
        <DataGrid
          onRowClick={handleRowClick}
          title='IME Cases'
          gridId='assigningQueueIME'
          enableUrlSync
          urlPrefix='ime'
          legacyParams={imeLegacyParams}
          columns={imeColumns}
          columnVisibilityModel={imeColumnVisibility}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="AssigningQueue_IME"
          defaultShowFilters={isInternal}
          enableAutoRefresh
        />
      </div>
    </div>
  );
};
